type OnCompleteFn = (
  id: string,
  blob: Blob,
  durationMs: number
) => Promise<void>;
const MAX_DURATION_MS = 30_000;

async function isStereo(audioBlob: Blob): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const audioContext = new window.AudioContext();
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target && event.target.result) {
        audioContext.decodeAudioData(
          event.target.result as ArrayBuffer,
          (buffer) => {
            if (buffer.numberOfChannels === 2) {
              resolve(true); // Stereo
            } else {
              resolve(false); // Mono or other
            }
          },
          (error) => {
            reject(error); // Decoding error
          }
        );
      } else {
        reject(new Error("FileReader result is null"));
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(audioBlob);
  });
}

async function checkBlobStereo(audioBlob: Blob) {
  try {
    const stereo = await isStereo(audioBlob);
    console.log(`Audio is stereo: ${stereo}`);
  } catch (error) {
    console.error("Error checking stereo:", error);
  }
}

const MIME_TYPE = "audio/mp4; codecs=mp4a.40.2";
let resolve: Function | null;

export const getRecordControl = async () => {
  let isRecording = false;
  let mediaRecorder: MediaRecorder;
  // to be given in component handling the recording
  let onComplete: OnCompleteFn | undefined;
  let timer = 0;

  const doRecord = async (word: string) => {
    let start = -1;
    let end = -1;
    try {
      if (!MediaRecorder.isTypeSupported(MIME_TYPE)) {
        alert(`Mime type ${MIME_TYPE} not supported`);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options = { mimeType: MIME_TYPE };
      mediaRecorder = new MediaRecorder(stream, options);
      let chunks: BlobPart[] = [];

      mediaRecorder.addEventListener("stop", async (_e: Event) => {
        // console.log(
        //   "Last data to read (after MediaRecorder.stop() called).",
        //   e
        // );
        end = performance.now();
        const blob = new Blob(chunks, { type: mediaRecorder.mimeType });

        checkBlobStereo(blob);

        chunks = [];

        if (!onComplete) {
          console.warn("No onComplete callback");
          return;
        }

        stream.getTracks().forEach((track) => track.stop());

        await onComplete(word, blob, end - start);
        if (resolve) {
          resolve();
        }
      });
      mediaRecorder.addEventListener("dataavailable", (e) => {
        chunks.push(e.data);
      });

      mediaRecorder.start();
      start = performance.now();
      timer = setTimeout(() => {
        isRecording = false;
        mediaRecorder.stop();
      }, MAX_DURATION_MS);
      // console.log("Recorder started.");
    } catch (e) {
      console.error("Get Audio", e);
      throw new Error(
        "Error getting audio permission. " + (e as Error).message
      );
    }
  };

  return {
    record: (word: string) => {
      if (isRecording) {
        return;
      }
      isRecording = true;
      resolve = null;
      return doRecord(word);
    },
    stop: () => {
      if (!isRecording) {
        return;
      }
      isRecording = false;
      clearTimeout(timer);

      mediaRecorder.stop();
      // if the user is recording, they hit on play,
      // we need to make sure we save info before playing the newly recorded sound
      const promise = new Promise((_resolve) => {
        resolve = _resolve;
      });
      return promise;
      // console.log("Recorder stopped.");
    },
    setOnComplete: (fn: OnCompleteFn) => (onComplete = fn),
  };
};
